import {
  GridEditInputCell,
  GridEditSingleSelectCellProps,
  GridRenderCellParams,
  GridRenderEditCellParams,
  GridPreProcessEditCellProps,
  GridEditCellProps,
  gridEditRowsStateSelector,
} from "@mui/x-data-grid-pro";
import { ComponentProps, FC } from "react";
import { GridBaseColDef } from "@mui/x-data-grid/models/colDef/gridColDef";
import { CaseItemKeys } from "../../../../../Cases/mappers/mapServerToLocalCase";
import { EMPTY_OBJECT } from "src/utils/empty-values";
import { Autocomplete, Box, Input, Tooltip } from "@mui/material";
import { GridColDef, useGridApiContext, useGridSelector } from "@mui/x-data-grid";
import {
  chargebackEscalationCaseStagesMap,
  allShortagesCaseItemStatusOptions,
  deniedReasonOptions,
  getDeniedReasonOption,
  DeniedReason,
  ShortagesEscalationCaseItemStatus,
  ChargebackEscalationCaseItemStatus,
  editShortagesCaseItemStatusOptions,
  getEscalationCaseItemStatusOption,
} from "src/pages/UserDashboard/Cases/api/constants.ts";
import TextField from "@mui/material/TextField";
import { CaseType } from "src/pages/UserDashboard/WorkPackets/Cases/case-types.ts";
import { CaseItemsActionButtons } from "../../../columns/CaseItemActionButtons";
import { BaseCaseItem, CaseItem } from "src/types/case-items.ts";
import { useFormikContext } from "formik";
import { getProperty } from "dot-prop";
import { Circle, CircleOutlined } from "@mui/icons-material";
import { ErrorTooltip } from "src/components/ErrorTooltip/ErrorTooltip";
import { ColoredOption } from "src/types/option";

/**
 * MUI Grid `renderCell` function cannot directly call any hooks. This function wraps a `renderCell` function
 * to treat it as a normal component such that it can use React hooks as normal.
 */
const wrapRenderCell =
  (
    Component: FC<GridRenderCellParams>,
    componentProps?: ComponentProps<typeof Component>,
  ): GridBaseColDef["renderCell"] =>
  props => <Component {...props} {...componentProps} />;

/**
 * MUI Grid `renderEditCell` function cannot directly call any hooks. This function wraps a `renderEditCell` function
 * to treat it as a normal component such that it can use React hooks as normal.
 */
const wrapRenderEditCell =
  (
    Component: FC<GridRenderEditCellParams>,
    componentProps?: ComponentProps<typeof Component>,
  ): GridBaseColDef["renderEditCell"] =>
  props => <Component {...props} {...componentProps} />;

const validateApprovedDate = (params: GridPreProcessEditCellProps): GridEditCellProps => {
  if (
    params.props.value === undefined ||
    params.otherFieldsProps!.approvedAmount.value === undefined ||
    params.otherFieldsProps!.approvedAmount.value === 0
  ) {
    return { ...params.props, error: "Approved Amount and Approved Date must both be set" };
  }
  return { ...params.props, error: false };
};

const validateApprovedAmount = (params: GridPreProcessEditCellProps): GridEditCellProps => {
  if (params.props.value === undefined || params.otherFieldsProps!.approvedDate.value === undefined) {
    return { ...params.props, error: "Approved Amount and Approved Date must both be set" };
  }
  return { ...params.props, error: false };
};

export const CaseItemColumnHeaders: Partial<Record<CaseItemKeys, string>> = {
  [CaseItemKeys.ChargeBackIssueId]: "Chargeback Issue ID",
  [CaseItemKeys.CaseItemStatus]: "Case Item Status",
  [CaseItemKeys.DeniedReason]: "Denied Reason",
  [CaseItemKeys.FinancialCharge]: "Financial Charge",
  [CaseItemKeys.PaidAmount]: "Paid Amount",
  [CaseItemKeys.ApprovedAmount]: "Approved Amount",
  [CaseItemKeys.ApprovedDate]: "Approved Date",
  [CaseItemKeys.WorkPacketId]: "Packet ID",
  [CaseItemKeys.VcDisputedInvoiceId]: "VC Disputed Invoice ID",
  [CaseItemKeys.LastChildInvoiceId]: "Last Child Invoice ID",
  [CaseItemKeys.VcParentInvoiceId]: "VC Parent Invoice ID",
  [CaseItemKeys.PoNumber]: "PO Number",
  [CaseItemKeys.ARN]: "ARN",
  [CaseItemKeys.ASN]: "ASN",
  [CaseItemKeys.ASIN]: "ASIN",
  [CaseItemKeys.RemainingOpenBalance]: "Remaining Open Balance",
  [CaseItemKeys.LastInvoiceNumberForPayment]: "Invoice # for Payment",
  [CaseItemKeys.LastInvoicePaymentId]: "Payment ID",
  [CaseItemKeys.LastInvoiceClientDate]: "C6 Invoices Client Date",
  [CaseItemKeys.LastInvoicePaymentDate]: "Payment Date",
  [CaseItemKeys.LastInvoiceClientAmount]: "C6 Invoices Client Amount",
  [CaseItemKeys.PaymentId]: "Payment ID",
  [CaseItemKeys.PaymentDate]: "Payment Date",
  [CaseItemKeys.RecoveredAmount]: "Recovered Amount",
  [CaseItemKeys.InvoiceForPayment]: "Invoice for Payment",
} as const;

type GridColOptions = Omit<GridBaseColDef, "field" | "headerName">;

function column(field: CaseItemKeys, options: GridColOptions = EMPTY_OBJECT): GridBaseColDef {
  if (options.renderCell) {
    options.renderCell = wrapRenderCell(options.renderCell);
  }

  if (options.renderEditCell) {
    options.renderEditCell = wrapRenderEditCell(options.renderEditCell);
  }

  return {
    field,
    headerName: CaseItemColumnHeaders[field],
    width: 200,
    sortable: false,
    filterable: false,
    editable: options.editable || Boolean(options.renderEditCell),
    ...options,
  };
}

function actionsColumn(): GridBaseColDef {
  return {
    field: "_actions",
    headerName: "",
    width: 200,
    display: "flex",
    sortable: false,
    filterable: false,
    renderCell: wrapRenderCell(CaseItemsActionButtons),
  };
}

function renderItemStatus(params: GridRenderEditCellParams) {
  const row = params.row as BaseCaseItem;
  const { color, title } = (row.caseType === CaseType.SHORTAGE_ESCALATION // skip settlements check  there is no status column there
    ? allShortagesCaseItemStatusOptions
    : chargebackEscalationCaseStagesMap
  ).find(action => action.value === params.value) || {
    color: "#EAECF0",
    title: params.value,
  };

  return (
    <Box display="flex" alignItems="center">
      {!!color && <Box width="12px" minWidth="12px" height="12px" bgcolor={color} borderRadius="2px" />}
      <Box ml={1}>{title}&nbsp;</Box>
    </Box>
  );
}

const renderMoneyCell = (params: GridRenderCellParams<any, number>) =>
  params.value != null
    ? new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(params.value)
    : "";

function GridInputCell(params: GridRenderEditCellParams) {
  const { error } = params;

  return (
    <ErrorTooltip open={!!error} title={error}>
      <GridEditInputCell {...params} />
    </ErrorTooltip>
  );
}

function renderDeniedReason(params: GridRenderCellParams) {
  if (!params.value) {
    return "";
  }

  const option = getDeniedReasonOption(params.value as DeniedReason);
  return (
    <Box display="flex" alignItems="center">
      <Box>{option.title}</Box>
    </Box>
  );
}

const renderDeniedReasonEditColumn = (props: GridEditSingleSelectCellProps) => {
  const apiRef = useGridApiContext();
  const row = props.row as any;

  const editRowsState = useGridSelector(apiRef, gridEditRowsStateSelector);
  const editRow = editRowsState[props.id];

  const editedStatus = editRow?.[CaseItemKeys.CaseItemStatus]?.value ?? row[CaseItemKeys.CaseItemStatus];

  const isDenied =
    editedStatus === ShortagesEscalationCaseItemStatus.DENIED ||
    editedStatus === ChargebackEscalationCaseItemStatus.REJECTED;

  const handleValueChange = async (_event: unknown, value: any) => {
    await apiRef.current.setEditCellValue({
      id: props.id,
      field: props.field,
      value: value?.value || null,
    });
  };

  const value = props.value ? deniedReasonOptions.find(option => option.value === props.value) : null;

  return (
    <Autocomplete
      options={deniedReasonOptions}
      onChange={handleValueChange}
      value={value}
      disabled={!isDenied}
      getOptionLabel={option => option.title}
      renderInput={params => <TextField {...params} variant="outlined" />}
    />
  );
};

const renderCaseItemStatusEditColumn = (props: GridEditSingleSelectCellProps) => {
  const apiRef = useGridApiContext();

  const handleValueChange = async (_event: unknown, value: any) => {
    await apiRef.current.setEditCellValue({
      id: props.id,
      field: props.field,
      value: value.value,
    });
  };

  const renderOption = (
    props: React.HTMLAttributes<HTMLLIElement> & {
      key: any;
    },
    option: ColoredOption<ShortagesEscalationCaseItemStatus>,
  ) => {
    return (
      <li className={`autocomplete-menu-item ${props.className}`} {...props} key={props.key}>
        <span className="autocomplete-menu-item__color-indicator" style={{ background: option.color }} />
        <span className="autocomplete-menu-item__text">{option.title}</span>
      </li>
    );
  };

  return (
    <Autocomplete
      options={editShortagesCaseItemStatusOptions}
      onChange={handleValueChange}
      value={getEscalationCaseItemStatusOption(props.value)}
      getOptionLabel={option => option.title}
      renderInput={params => <TextField {...params} variant="outlined" />}
      renderOption={renderOption}
    />
  );
};

const chargebackEscalationItemColumns: GridColDef[] = [
  column(CaseItemKeys.ChargeBackIssueId, { flex: 1, minWidth: 200, sortable: true }),
  column(CaseItemKeys.CaseItemStatus, {
    renderCell: renderItemStatus,
    renderEditCell: renderCaseItemStatusEditColumn,
    width: 250,
    sortable: true,
  }),
  column(CaseItemKeys.DeniedReason, {
    renderCell: renderDeniedReason,
    renderEditCell: renderDeniedReasonEditColumn,
    width: 250,
    sortable: true,
  }),
  column(CaseItemKeys.FinancialCharge, { sortable: true, renderCell: renderMoneyCell, type: "number" }),
  column(CaseItemKeys.ApprovedAmount, {
    sortable: true,
    renderCell: renderMoneyCell,
    editable: true,
    type: "number",
    renderEditCell: GridInputCell,
    preProcessEditCellProps: validateApprovedAmount,
  }),
  column(CaseItemKeys.ApprovedDate, {
    sortable: true,
    editable: true,
    type: "date",
    preProcessEditCellProps: validateApprovedDate,
  }),
  column(CaseItemKeys.PaidAmount, { sortable: true, renderCell: renderMoneyCell, type: "number" }),
  column(CaseItemKeys.ASIN, { sortable: false, type: "string" }),
  column(CaseItemKeys.PoNumber, { sortable: false, type: "string" }),
  column(CaseItemKeys.ARN, { sortable: true, type: "string", editable: true }),
  column(CaseItemKeys.ASN, { sortable: true, type: "string", editable: true }),
  actionsColumn(),
];

const shortageEscalationItemColumns: GridColDef[] = [
  column(CaseItemKeys.WorkPacketId, { flex: 1, minWidth: 200, sortable: true }),
  column(CaseItemKeys.VcParentInvoiceId, { sortable: true }),
  column(CaseItemKeys.LastChildInvoiceId, { sortable: true }),
  column(CaseItemKeys.CaseItemStatus, {
    renderCell: params => {
      const itemStatus = renderItemStatus(params);
      const isPaidOut = params.row[CaseItemKeys.CaseItemStatus] === ShortagesEscalationCaseItemStatus.PAID_OUT;
      return (
        <>
          {isPaidOut ? (
            <Tooltip title={"This case item is Paid Out. The Case Item Status cannot be updated."}>
              {itemStatus}
            </Tooltip>
          ) : (
            itemStatus
          )}
        </>
      );
    },
    renderEditCell: renderCaseItemStatusEditColumn,
    width: 250,
    sortable: true,
  }),
  column(CaseItemKeys.DeniedReason, {
    renderCell: renderDeniedReason,
    renderEditCell: renderDeniedReasonEditColumn,
    width: 250,
    sortable: true,
  }),
  column(CaseItemKeys.PoNumber, { sortable: true, editable: true, type: "number" }),
  column(CaseItemKeys.ASN, { sortable: true, editable: true, type: "string" }), // what format this should be?
  column(CaseItemKeys.ARN, { sortable: true, editable: true, type: "string" }), // what format this should be?
  column(CaseItemKeys.ApprovedAmount, {
    renderCell: params => {
      const isPaidOut = params.row[CaseItemKeys.CaseItemStatus] === ShortagesEscalationCaseItemStatus.PAID_OUT;
      const moneyCell = renderMoneyCell(params);
      return (
        <>
          {isPaidOut ? (
            <Tooltip title={"This case item is Paid Out. The Approved Amount cannot be updated."}>
              <span>{moneyCell}</span>
            </Tooltip>
          ) : (
            moneyCell
          )}
        </>
      );
    },
    sortable: false,
    editable: true,
    type: "number",
    renderEditCell: GridInputCell,
    preProcessEditCellProps: params => {
      if (params.props.value === null) {
        // we got invalid input
        return { ...params.props, error: "Approved Amount must be a number with no symbols" };
      }

      if (params.props.value > params.row.remainingOpenBalance) {
        return { ...params.props, error: "Approved amount should be less than or equal to Remaining Open Balance" };
      }
      return { ...params.props, error: false };
    },
  }),
  column(CaseItemKeys.RemainingOpenBalance, {
    sortable: true,
    type: "number",
    renderCell: renderMoneyCell,
  }),
  actionsColumn(),
];

const shortageSettlementItemColumns: GridColDef[] = [
  column(CaseItemKeys.WorkPacketId, { flex: 1, minWidth: 200, sortable: true }),
  column(CaseItemKeys.LastChildInvoiceId, { sortable: true }),
  column(CaseItemKeys.ASN, { sortable: true, editable: true, type: "string" }),
  column(CaseItemKeys.ARN, { sortable: true, editable: true, type: "string" }),
  column(CaseItemKeys.PoNumber, { sortable: true, editable: true, type: "string" }),
  actionsColumn(),
];

export const columnsMap: Record<CaseType, GridColDef[]> = {
  [CaseType.SHORTAGE_ESCALATION]: shortageEscalationItemColumns,
  [CaseType.CHARGEBACK_ESCALATION]: chargebackEscalationItemColumns,
  [CaseType.SHORTAGE_SETTLEMENT]: shortageSettlementItemColumns,
};

export const disputeAmountPopupItemColumns: GridColDef[] = [
  column(CaseItemKeys.VcParentInvoiceId, { flex: 1 }),
  column(CaseItemKeys.LastChildInvoiceId, { flex: 1 }),
  column(CaseItemKeys.RemainingOpenBalance, {
    flex: 1,
    type: "number",
    display: "flex",
    renderCell: params => {
      const { setFieldValue, values, initialValues } = useFormikContext<CaseItem[]>();
      const index = values.findIndex(item => item.id === params.id);
      const fieldKey = `[${index}].${params.field}`;
      const initialValue = getProperty(initialValues, fieldKey) as number;

      return (
        <>
          <Input
            disableUnderline
            id={params.id.toString()}
            key={params.id.toString()}
            sx={{ border: "2px solid black", borderRadius: "4px" }}
            inputProps={{ style: { textAlign: "right" } }}
            type="number"
            value={params.value}
            onChange={event => setFieldValue(fieldKey, Number(event.currentTarget.value))}
            onKeyDown={event => {
              if (["ArrowDown", "ArrowUp", "ArrowLeft", "ArrowRight"].includes(event.key)) {
                event.stopPropagation();
              }
              if (event.key === "Enter") {
                const form = event.currentTarget.form;
                if (!form) return;
                const formInputs = Array.from(form.elements).filter(element => element.nodeName === "INPUT");
                const index = formInputs.indexOf(event.currentTarget);
                const maxIndex = formInputs.length - 1;
                const nextIndex = index + 1 > maxIndex ? 0 : index + 1;

                const nextElement = formInputs[nextIndex];

                if (nextElement instanceof HTMLElement) {
                  event.preventDefault();
                  nextElement.focus();
                }
              }
              if (event.key === "Escape") {
                const focusableParent = event.currentTarget.closest("[tabindex]");

                if (focusableParent instanceof HTMLElement) {
                  event.stopPropagation();
                  focusableParent.focus();
                }
              }
            }}
          />
          {initialValue !== params.value ? (
            <Tooltip title={`Amount has been updated from ${initialValue} to ${params.value}`}>
              <Circle sx={{ fontSize: 18, paddingLeft: "4px", color: "#b0b0b0" }} />
            </Tooltip>
          ) : (
            <Tooltip title="Amount is unchanged">
              <CircleOutlined sx={{ fontSize: 18, paddingLeft: "4px", color: "#b0b0b0" }} />
            </Tooltip>
          )}
        </>
      );
    },
  }),
  column(CaseItemKeys.PoNumber, { flex: 1, type: "string" }),
];
